import { useState, useEffect, useCallback } from 'react';
import { apiRawFetch } from '../../main/client';

export type RecurrenceType = 'monthly' | 'quarterly' | 'yearly';

export interface Expense {
    id: number;
    propertyId: number;
    categoryId: number;
    category: {
        id: number;
        name: string;
        type: string;
        icon: string | null;
        color: string | null;
    } | null;
    amount: number;
    description: string | null;
    date: string;
    isRecurring: boolean;
    recurrenceType: RecurrenceType | null;
    recurrenceEndDate: string | null;
    cleaningId: number | null;
    isAutoGenerated: boolean;
    created: string;
    updated: string;
}

export interface ExpenseFilters {
    categoryId?: number;
    startDate?: string;
    endDate?: string;
    type?: 'all' | 'one-time' | 'recurring';
    // URL-persisted date components for UI
    fromYear?: number;
    fromMonth?: number;
    fromDay?: number;
    toYear?: number;
    toMonth?: number;
    toDay?: number;
}

export interface CreateExpenseInput {
    categoryId: number;
    amount: number;
    description?: string;
    date: string;
    isRecurring?: boolean;
    recurrenceType?: RecurrenceType;
    recurrenceEndDate?: string;
}

export interface UpdateExpenseInput extends Partial<CreateExpenseInput> {}

interface UseExpensesResult {
    expenses: Expense[];
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    createExpense: (input: CreateExpenseInput) => Promise<Expense | null>;
    updateExpense: (expenseId: number, input: UpdateExpenseInput) => Promise<Expense | null>;
    deleteExpense: (expenseId: number) => Promise<boolean>;
}

const useExpenses = (propertyId: number | undefined, filters?: ExpenseFilters): UseExpensesResult => {
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchExpenses = useCallback(async () => {
        if (!propertyId) {
            setExpenses([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();
            if (filters) {
                if (filters.categoryId) params.set('categoryId', filters.categoryId.toString());
                if (filters.startDate) params.set('startDate', filters.startDate);
                if (filters.endDate) params.set('endDate', filters.endDate);
                if (filters.type && filters.type !== 'all') params.set('type', filters.type);
            }

            const queryString = params.toString();
            const url = `api/properties/${propertyId}/expenses${queryString ? `?${queryString}` : ''}`;

            const response = await apiRawFetch(url);

            if (!response.ok) {
                throw new Error('Failed to fetch expenses');
            }

            const { data } = await response.json();
            setExpenses(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [propertyId, filters]);

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    const createExpense = useCallback(async (input: CreateExpenseInput): Promise<Expense | null> => {
        if (!propertyId) return null;

        try {
            const response = await apiRawFetch(
                `api/properties/${propertyId}/expenses`,
                'POST',
                input
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create expense');
            }

            const { data } = await response.json();
            await fetchExpenses();
            return data;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            return null;
        }
    }, [propertyId, fetchExpenses]);

    const updateExpense = useCallback(async (expenseId: number, input: UpdateExpenseInput): Promise<Expense | null> => {
        if (!propertyId) return null;

        try {
            const response = await apiRawFetch(
                `api/properties/${propertyId}/expenses/${expenseId}`,
                'POST',
                input
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update expense');
            }

            const { data } = await response.json();
            await fetchExpenses();
            return data;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            return null;
        }
    }, [propertyId, fetchExpenses]);

    const deleteExpense = useCallback(async (expenseId: number): Promise<boolean> => {
        if (!propertyId) return false;

        try {
            const response = await apiRawFetch(
                `api/properties/${propertyId}/expenses/${expenseId}`,
                'DELETE'
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete expense');
            }

            await fetchExpenses();
            return true;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            return false;
        }
    }, [propertyId, fetchExpenses]);

    return {
        expenses,
        loading,
        error,
        refresh: fetchExpenses,
        createExpense,
        updateExpense,
        deleteExpense,
    };
};

export default useExpenses;
